import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LIMITES } from '../limites';
import './VerbDash.css';

const GAME_DURATION = 60;

const suggestedThemes = [
    { name: 'Viajes', icon: '✈️', colors: 'from-blue-500 to-cyan-400' },
    { name: 'Cocina', icon: '🍳', colors: 'from-orange-500 to-amber-400' },
    { name: 'Tecnología', icon: '💻', colors: 'from-indigo-500 to-purple-500' },
    { name: 'Negocios', icon: '💼', colors: 'from-gray-600 to-gray-800' },
    { name: 'Salud', icon: '🩺', colors: 'from-red-500 to-rose-500' },
    { name: 'Naturaleza', icon: '🌳', colors: 'from-green-500 to-lime-500' },
    { name: 'Deportes', icon: '⚽️', colors: 'from-teal-500 to-emerald-500' },
    { name: 'Arte', icon: '🎨', colors: 'from-pink-500 to-fuchsia-500' },
    { name: 'Ciencia', icon: '🔬', colors: 'from-sky-600 to-sky-400' },
    { name: 'Música', icon: '🎵', colors: 'from-violet-600 to-purple-600' },
];

function VerbDash() {
    const [gameState, setGameState] = useState('menu');
    const [userTheme, setUserTheme] = useState('');
    const [verbs, setVerbs] = useState([]);
    const [currentVerbIndex, setCurrentVerbIndex] = useState(0);
    const [gameStep, setGameStep] = useState(1);
    
    const [seenVerbsInSession, setSeenVerbsInSession] = useState({});

    const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(
        // Bug #4 fix: localStorage siempre devuelve string — parsear a número
        () => parseInt(localStorage.getItem('verbDashHighScore') || '0', 10)
    );
    
    const [feedback, setFeedback] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [step2Answers, setStep2Answers] = useState({ past: null, participle: null });
    const [step2Options, setStep2Options] = useState([]);
    const [sentenceAnswers, setSentenceAnswers] = useState({ present: null, past: null, participle: null });
    const [shuffledOptions, setShuffledOptions] = useState([]);

    const hasProcessedStep2 = useRef(false);
    const hasProcessedStep3 = useRef(false);

    const currentVerb = verbs[currentVerbIndex];

    useEffect(() => {
        if (gameState !== 'playing') return;
        if (timeLeft <= 0) {
            setGameState('gameOver');
            return;
        }
        const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [gameState, timeLeft]);

    const fetchVerbs = async (theme) => {
        if (!theme) {
            setError('Por favor, escribe o selecciona un tema.');
            return;
        }
        setIsLoading(true);
        setError('');
        setGameState('loading');
        const token = localStorage.getItem('token');

        const seenVerbsForThisTheme = seenVerbsInSession[theme] || [];

        try {
            // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
            const response = await fetch('/api/minigames/generate-verbs', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ theme, seen_verbs: seenVerbsForThisTheme }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'No se pudieron generar los verbos.');
            }
            
            const data = await response.json();

            setStep2Answers({ past: null, participle: null });
            setSentenceAnswers({ present: null, past: null, participle: null });

            setVerbs(data);
            setCurrentVerbIndex(0);
            setScore(0);
            setGameState('breakpoint');
        } catch (err) {
            setError(err.message);
            setGameState('menu');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleEndOfGame = useCallback(async () => {
        if (userTheme && verbs.length > 0) {
            const newVerbsLearned = verbs.map(v => v.english_present);
            setSeenVerbsInSession(prev => {
                const updatedSeen = new Set([...(prev[userTheme] || []), ...newVerbsLearned]);
                return { ...prev, [userTheme]: Array.from(updatedSeen) };
            });
        }

        if (score > highScore) {
            localStorage.setItem('verbDashHighScore', score);
            setHighScore(score);
        }
        
        // --- GUARDAR XP EN EL BACKEND ---
        try {
            const token = localStorage.getItem('token');
            if (token && verbs.length > 0) {
                // Cada verbo completado suma hasta 300 puntos en teoría (100 por fase).
                // Calculamos respuestas correctas en base al score
                const correct_answers = Math.floor(score / 100);
                
                await fetch('/api/minigames/save-score', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        game_name: 'verb_dash',
                        score: score,
                        correct_answers: correct_answers,
                        total_questions: verbs.length * 3
                    })
                });
            }
        } catch (err) {
            console.error("No se pudo guardar el score en la base de datos:", err);
        }

        setGameState('results');
    }, [userTheme, verbs, score, highScore]);

    const backToMenu = () => {
        // Reset game state completamente para evitar que useEffects de Step 2/3
        // vuelvan a disparar con sentenceAnswers/step2Answers viejos.
        // Bug: resetear solo los refs causaba que el useEffect de Step 3 re-corriera
        // con los sentenceAnswers del último verbo → handleCorrectAnswer → loop infinito.
        setGameState('menu');
        setUserTheme('');
        setError('');

        // Reset de estado de juego
        setVerbs([]);
        setCurrentVerbIndex(0);
        setGameStep(1);
        setScore(0);
        setTimeLeft(GAME_DURATION);
        setFeedback(null);

        // Reset de respuestas — crítico para detener useEffects de Step 2 y 3
        setStep2Answers({ past: null, participle: null });
        setSentenceAnswers({ present: null, past: null, participle: null });
        setStep2Options([]);
        setShuffledOptions([]);

        // Reset de refs
        hasProcessedStep2.current = false;
        hasProcessedStep3.current = false;
    };

    const startGame = () => {
        setGameStep(1);
        setTimeLeft(GAME_DURATION);
        setGameState('playing');
    };

    const showFeedback = useCallback((type) => {
        setFeedback(type);
        setTimeout(() => {
            setFeedback(null);
            setGameState(prev => {
                if (prev !== 'results' && prev !== 'gameOver') {
                    return 'playing';
                }
                return prev;
            });
        }, 1200); // Duración de la animación del popup
    }, []);
    
    const nextStepOrVerb = useCallback(() => {
        setTimeout(() => {
            setGameStep(s => {
                if (s < 3) {
                    return s + 1;
                } else {
                    if (currentVerbIndex < verbs.length - 1) {
                        setCurrentVerbIndex(i => i + 1);
                        setGameState('breakpoint');
                        setStep2Answers({ past: null, participle: null });
                        setSentenceAnswers({ present: null, past: null, participle: null });
                        return 1;
                    } else {
                        handleEndOfGame();
                        return 3;
                    }
                }
            });
        }, 1200);
    }, [currentVerbIndex, verbs.length, handleEndOfGame]);

    const handleCorrectAnswer = useCallback(() => {
        setScore(s => s + 100);
        setTimeLeft(t => t + 10);
        showFeedback('correct');
        nextStepOrVerb();
    }, [nextStepOrVerb, showFeedback]);

    const handleIncorrectAnswer = useCallback(() => {
        setTimeLeft(t => Math.max(0, t - 5));
        showFeedback('incorrect');
    }, [showFeedback]);

    const handleStep2Selection = (selectedVerb) => {
        if (step2Answers.past && step2Answers.participle) return;
        if (!step2Answers.past) {
            setStep2Answers(prev => ({...prev, past: selectedVerb}));
        } else {
            setStep2Answers(prev => ({...prev, participle: selectedVerb}));
        }
    };

    useEffect(() => {
        if (!currentVerb || !step2Answers.past || !step2Answers.participle || hasProcessedStep2.current) {
            return;
        }
        
        hasProcessedStep2.current = true;

        const isCorrect = step2Answers.past === currentVerb.english_past && step2Answers.participle === currentVerb.english_participle;
        if (isCorrect) {
            handleCorrectAnswer();
        } else {
            handleIncorrectAnswer();
            setTimeout(() => {
                setStep2Answers({ past: null, participle: null });
                hasProcessedStep2.current = false;
            }, 1200);
        }
    }, [step2Answers, currentVerb, handleCorrectAnswer, handleIncorrectAnswer]);

    const checkSentence = (chosenVerb) => {
        if (!currentVerb) return;
        const { english_present, english_past, english_participle } = currentVerb;

        let correctTense = null;
        if (!sentenceAnswers.present && chosenVerb === english_present) correctTense = 'present';
        else if (!sentenceAnswers.past && chosenVerb === english_past) correctTense = 'past';
        else if (!sentenceAnswers.participle && chosenVerb === english_participle) correctTense = 'participle';

        if (correctTense) {
            setSentenceAnswers(prev => ({ ...prev, [correctTense]: chosenVerb }));
        } else {
            // Bug #3 fix (v2): solo ignorar silenciosamente si el verbo ya está colocado
            // en TODOS los slots que lo aceptan (evita penalizar re-clicks inocentes).
            // Esto cubre verbos regulares donde past === participle — el mismo valor
            // debe poder colocarse dos veces en distintos slots.
            const placedInPresent   = sentenceAnswers.present   === chosenVerb && english_present   === chosenVerb;
            const placedInPast      = sentenceAnswers.past       === chosenVerb && english_past       === chosenVerb;
            const placedInParticiple= sentenceAnswers.participle === chosenVerb && english_participle === chosenVerb;

            const needsPresent   = english_present   === chosenVerb;
            const needsPast      = english_past       === chosenVerb;
            const needsParticiple= english_participle === chosenVerb;

            const fullyPlaced =
                (!needsPresent    || placedInPresent)    &&
                (!needsPast       || placedInPast)       &&
                (!needsParticiple || placedInParticiple);

            if (!fullyPlaced) {
                handleIncorrectAnswer();
            }
            // Si ya está colocado en todos sus slots → ignorar silenciosamente (no penalizar)
        }
    };

    useEffect(() => {
        if (!currentVerb || !sentenceAnswers.present || !sentenceAnswers.past || !sentenceAnswers.participle || hasProcessedStep3.current) {
            return;
        }

        hasProcessedStep3.current = true;

        const allCorrect = 
            sentenceAnswers.present === currentVerb.english_present &&
            sentenceAnswers.past === currentVerb.english_past &&
            sentenceAnswers.participle === currentVerb.english_participle;
        if (allCorrect) {
            handleCorrectAnswer();
        }
    }, [sentenceAnswers, currentVerb, handleCorrectAnswer]);

    useEffect(() => {
        if (currentVerb) {
            hasProcessedStep2.current = false;
            hasProcessedStep3.current = false;

            // Bug #5 fix (v2): generar distractores que NUNCA coincidan con las respuestas correctas.
            // Para verbos regulares (packed/packed), present+'ed' == english_past → colisión.
            // Filtramos todos los candidatos contra el Set de respuestas correctas.
            const past        = currentVerb.english_past;
            const participle  = currentVerb.english_participle;
            const present     = currentVerb.english_present;
            const correctAnswerSet = new Set([past, participle]);

            const distractorCandidates = [
                present + 'ing',  // "packing", "going"  — nunca coincide con past/participle
                present + 's',    // "packs", "gos"       — idem
                present + 'er',   // "packer", "goer"     — fallback extra
                past    + 'ing',  // "packeding"           — obviamente incorrecto, útil como 4to
            ].filter(d => !correctAnswerSet.has(d));    // eliminar si por casualidad coincide

            const uniqueDistractors = [...new Set(distractorCandidates)].slice(0, 2);

            // Opciones finales: past + participle (puede repetir en verbos regulares) + 2 distractores reales
            setStep2Options(
                [past, participle, ...uniqueDistractors].sort(() => Math.random() - 0.5)
            );
            setShuffledOptions(
                [currentVerb.english_present, currentVerb.english_past, currentVerb.english_participle].sort(() => Math.random() - 0.5)
            );
        }
    }, [currentVerb]);

    const handleThemeChange = (e) => {
        const value = e.target.value;
        if (/\s/.test(value)) {
            setError('El tema debe ser una sola palabra.');
        } else {
            setError('');
            setUserTheme(value);
        }
    };

    const renderMenu = () => (
        <div className="game-menu">
            <h2>Verb Dash Challenge</h2>
            <p>Escribe un tema o elige una de las sugerencias para comenzar tu desafío.</p>
            <input
                maxLength={LIMITES.contexto}
                type="text" 
                className="theme-input" 
                placeholder="Ej: Finanzas, Medicina, Espacio..."
                value={userTheme} 
                onChange={handleThemeChange}
            />
            <button className="start-game-btn" onClick={() => fetchVerbs(userTheme)} disabled={isLoading || !userTheme}>
                {isLoading ? 'Generando...' : 'Crear Desafío'}
            </button>
            {error && <p style={{color: '#ef4444', marginTop: '1rem'}}>{error}</p>}
            <div className="theme-suggestions">
                <p>O elige un tema popular:</p>
                <div className="suggestions-grid">
                    {suggestedThemes.map(theme => (
                        <button key={theme.name} className={`suggestion-btn ${theme.colors}`}
                            onClick={() => { 
                                setUserTheme(theme.name); 
                                setError('');
                                fetchVerbs(theme.name); 
                            }}>
                            <span className="suggestion-icon">{theme.icon}</span>
                            <span>{theme.name}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderBreakpoint = () => (
        <div className="breakpoint-screen">
            <h3>PREPÁRATE...</h3>
            <div className="verb-card-es">
                <h2>{currentVerb.spanish_present}</h2>
                <div className="verb-forms-es">
                    <div className="form-es"><p>Presente</p><span>{currentVerb.spanish_present}</span></div>
                    <div className="form-es"><p>Pasado</p><span>{currentVerb.spanish_past}</span></div>
                    <div className="form-es"><p>Participio</p><span>{currentVerb.spanish_participle}</span></div>
                </div>
            </div>
            <button className="start-game-btn" onClick={startGame}>Comenzar Ronda</button>
        </div>
    );
    
    const renderGame = () => (
        <div>
            <div className="game-header">
                <div>Verbo {currentVerbIndex + 1} de {verbs.length}</div>
                <div className="game-stats">
                    <span className={`timer ${timeLeft <= 10 ? 'warning' : ''}`}>⏰ {timeLeft}s</span>
                    <span>⭐ {score}</span>
                </div>
            </div>
            <div className="progress-bar-container">
                <div 
                className={`progress-bar ${timeLeft <= 10 ? 'warning' : ''}`} 
                style={{ width: `${(timeLeft / GAME_DURATION) * 100}%` }}
                ></div>
            </div>
            <div className="game-step">
                {gameStep === 1 && renderStep1()}
                {gameStep === 2 && renderStep2()}
                {gameStep === 3 && renderStep3()}
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div>
            <h3>Paso 1: ¿Regular o Irregular?</h3>
            <div className="verb-display-en"><h2>{currentVerb.english_present}</h2></div>
            <div className="choice-buttons">
                <button className="btn-gamer" onClick={() => currentVerb.type === 'regular' ? handleCorrectAnswer() : handleIncorrectAnswer()}>Regular</button>
                <button className="btn-gamer" onClick={() => currentVerb.type === 'irregular' ? handleCorrectAnswer() : handleIncorrectAnswer()}>Irregular</button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div>
            <h3>Paso 2: Elige las Formas Correctas</h3>
            <div className="write-forms-grid">
                <div className="form-en"><p>Presente</p><h2>{currentVerb.english_present}</h2></div>
                <div className="form-en">
                    <p>Pasado</p>
                    <div className="form-en-slot">{step2Answers.past || '?'}</div>
                </div>
                <div className="form-en">
                    <p>Participio</p>
                    <div className="form-en-slot">{step2Answers.participle || '?'}</div>
                </div>
            </div>
            <div className="form-options-grid">
                {step2Options.map((opt, index) => (
                    <button key={`${opt}-${index}`} className="btn-gamer" onClick={() => handleStep2Selection(opt)}>
                        {opt}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderStep3 = () => {
        const splitSentence = (fullSentence, verb) => {
            if (!fullSentence || !verb) return [fullSentence || '', ''];
            const index = fullSentence.toLowerCase().indexOf(verb.toLowerCase());
            if (index === -1) {
                return [fullSentence.replace(/___/g, ''), ''];
            }
            const part1 = fullSentence.substring(0, index);
            const part2 = fullSentence.substring(index + verb.length);
            return [part1, part2];
        };

        const presentParts = splitSentence(currentVerb.sentence_present, currentVerb.english_present);
        const pastParts = splitSentence(currentVerb.sentence_past, currentVerb.english_past);
        const participleParts = splitSentence(currentVerb.sentence_participle, currentVerb.english_participle);

        return (
            <div>
                <h3>Paso 3: Completa las Oraciones</h3>
                <div className="sentences-container">
                    <div className="sentence-box">
                        <span>{presentParts[0]}</span>
                        <div className="sentence-blank">{sentenceAnswers.present}</div>
                        <span>{presentParts[1]}</span>
                    </div>
                    <div className="sentence-box">
                        <span>{pastParts[0]}</span>
                        <div className="sentence-blank">{sentenceAnswers.past}</div>
                        <span>{pastParts[1]}</span>
                    </div>
                    <div className="sentence-box">
                        <span>{participleParts[0]}</span>
                        <div className="sentence-blank">{sentenceAnswers.participle}</div>
                        <span>{participleParts[1]}</span>
                    </div>
                </div>
                <div className="verb-options">
                    {shuffledOptions.map((opt, index) => (
                        <button key={`${opt}-${index}`} className="btn-gamer" onClick={() => checkSentence(opt)}>
                            {opt}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    const renderResults = (isGameOver = false) => (
        <div className="results-screen">
            <h2>{isGameOver ? '¡Tiempo Agotado!' : '¡Juego Terminado!'}</h2>
            <p>Tu puntuación final fue:</p>
            <h3 style={{fontSize: '4rem', margin: '1rem 0'}}>{score}</h3>
            <p>Mejor Puntuación: {highScore}</p>
            
            <div className="learned-verbs-summary">
                <h4>Verbos que practicaste:</h4>
                <div className="learned-verbs-list">
                    {verbs.map(verb => (
                        <div key={verb.english_present} className="learned-verb-item">
                            <span className="verb-english">{verb.english_present} ({verb.type})</span>
                            <span className="verb-spanish">{verb.spanish_present}</span>
                        </div>
                    ))}
                </div>
            </div>

            <button className="start-game-btn" onClick={backToMenu}>Jugar de Nuevo</button>
        </div>
    );

    return (
        <div className="verb-dash-container">
            <div className="verb-dash-inner-container">
                {feedback && <div className={`feedback-popup feedback-${feedback}`}>{feedback === 'correct' ? '🔥 Excellent!' : '😅 Try again'}</div>}
                {gameState === 'menu' && renderMenu()}
                {gameState === 'loading' && <p style={{textAlign: 'center', padding: '2rem'}}>Cargando tu desafío...</p>}
                {gameState === 'breakpoint' && currentVerb && renderBreakpoint()}
                {gameState === 'playing' && currentVerb && renderGame()}
                {gameState === 'results' && renderResults()}
                {gameState === 'gameOver' && renderResults(true)}
            </div>
        </div>
    );
}

export default VerbDash;